import { createContext, useContext } from 'react';

/**
 * Lets a taxonomy chip anywhere in the agent surface drive the list it sits
 * in. The filter state lives in `useAgentFilters` (session storage, shared by
 * every instance under one key), but the chips that offer "filter to this"
 * render several layers down — inside the association strip on a row or a
 * stream header — with no prop path to it.
 *
 * Providers hand down the current selection and its setters; consumers that
 * render outside a provider (a plugin surface, a story) simply don't offer
 * the action, so a chip degrades to its other actions rather than breaking.
 */
export interface AgentListFilterActions {
  /** The group the list is currently narrowed to, or null. */
  activeGroupId: string | null;
  /** Narrow the list to a group, or clear it with null. */
  setGroupFilter: (groupId: string | null) => void;
  /** The label the list is currently narrowed to, or null. */
  activeLabelId: string | null;
  /** Narrow the list to a label, or clear it with null. */
  setLabelFilter: (labelId: string | null) => void;
}

export const AgentListFilterContext = createContext<AgentListFilterActions | null>(null);

/** The ambient list-filter controls, or null outside a provider. */
export function useAgentListFilterActions(): AgentListFilterActions | null {
  return useContext(AgentListFilterContext);
}
